import { Injectable } from '@angular/core';

import { ApiService } from '../api/api.service';

export type AiAssistantEnvironment = 'qa';
export type AiAssistantMessageRole = 'user' | 'assistant' | 'system';

export interface AiAssistantCitation {
  type: string;
  id?: string;
  label?: string;
  url?: string;
}

export interface AiAssistantToolCall {
  tool?: string;
  summary?: string;
}

export interface AiAssistantAttachment {
  fileId?: string;
  fileUrl?: string;
  fileData?: string;
  filename?: string;
  mediaType?: string;
}

export interface AiAssistantChatRequest {
  conversationId?: string;
  message: string;
  attachments?: AiAssistantAttachment[];
  context: {
    teamId?: string;
    companyId?: string;
    environment: AiAssistantEnvironment;
  };
  client: {
    surface: 'teamadmin';
    timezone: string;
  };
  stream: false;
}

export interface AiAssistantChatResponse {
  success?: boolean;
  conversationId: string;
  messageId?: string;
  answer: string;
  toolCalls: AiAssistantToolCall[];
  citations: AiAssistantCitation[];
  stream?: boolean;
}

export interface AiAssistantConversationMessage {
  id: string;
  role: AiAssistantMessageRole;
  content: string;
  attachments: AiAssistantAttachment[];
  toolCalls: AiAssistantToolCall[];
  citations: AiAssistantCitation[];
  createdAt?: string;
}

export interface AiAssistantConversationResponse {
  conversationId: string;
  messages: AiAssistantConversationMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class AiAssistantService {
  constructor(private readonly apiService: ApiService) {}

  async sendMessage(request: AiAssistantChatRequest): Promise<AiAssistantChatResponse> {
    const response = await this.apiService.post('v5/admin/ai/chat', request as any);
    const payload = this.unwrapPayload(response);

    return {
      success: payload?.success,
      conversationId: String(payload?.conversationId || request.conversationId || ''),
      messageId: this.toOptionalString(payload?.messageId || payload?.id),
      answer: this.extractText(payload, ['answer', 'message', 'content', 'text']),
      toolCalls: this.normalizeToolCalls(payload?.toolCalls),
      citations: this.normalizeCitations(payload?.citations),
      stream: !!payload?.stream,
    };
  }

  async getConversation(conversationId: string): Promise<AiAssistantConversationResponse> {
    const response = await this.apiService.get(`v5/admin/ai/chat/${conversationId}`);
    const payload = this.unwrapPayload(response);
    const conversation = payload?.conversation || payload;
    const rawMessages = Array.isArray(conversation?.messages)
      ? conversation.messages
      : Array.isArray(payload?.messages)
        ? payload.messages
        : [];

    const messages = rawMessages
      .map((message: any, index: number) => this.normalizeConversationMessage(message, index))
      .filter((message: AiAssistantConversationMessage | null): message is AiAssistantConversationMessage => !!message);

    if (!messages.length) {
      const fallbackAnswer = this.extractText(conversation, ['answer', 'message', 'content', 'text']);
      if (fallbackAnswer) {
        messages.push({
          id: `${conversationId}-assistant`,
          role: 'assistant',
          content: fallbackAnswer,
          attachments: this.normalizeAttachments(conversation?.attachments),
          toolCalls: this.normalizeToolCalls(conversation?.toolCalls),
          citations: this.normalizeCitations(conversation?.citations),
          createdAt: this.toOptionalString(conversation?.createdAt),
        });
      }
    }

    return {
      conversationId: String(conversation?.conversationId || payload?.conversationId || conversationId),
      messages,
    };
  }

  private normalizeConversationMessage(
    message: any,
    index: number,
  ): AiAssistantConversationMessage | null {
    const role = this.normalizeRole(message?.role);
    const content = this.extractText(message, ['answer', 'message', 'content', 'text']);
    const attachments = this.normalizeAttachments(message?.attachments);

    if (!role || (!content && attachments.length === 0)) {
      return null;
    }

    return {
      id: String(message?.id || message?.messageId || `${role}-${index}`),
      role,
      content,
      attachments,
      toolCalls: this.normalizeToolCalls(message?.toolCalls),
      citations: this.normalizeCitations(message?.citations),
      createdAt: this.toOptionalString(message?.createdAt || message?.updatedAt),
    };
  }

  private normalizeRole(value: unknown): AiAssistantMessageRole | null {
    if (value === 'user' || value === 'assistant' || value === 'system') {
      return value;
    }

    return null;
  }

  private normalizeToolCalls(value: unknown): AiAssistantToolCall[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item: any) => ({
      tool: this.toOptionalString(item?.tool || item?.name || item?.type),
      summary: this.toOptionalString(item?.summary || item?.description || item?.message),
    }));
  }

  private normalizeCitations(value: unknown): AiAssistantCitation[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item: any) => ({
      type: String(item?.type || 'source'),
      id: this.toOptionalString(item?.id),
      label: this.toOptionalString(item?.label || item?.title || item?.id || item?.url),
      url: this.toOptionalString(item?.url),
    }));
  }

  private normalizeAttachments(value: unknown): AiAssistantAttachment[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item: any) => ({
        fileId: this.toOptionalString(item?.fileId),
        fileUrl: this.toOptionalString(item?.fileUrl),
        filename: this.toOptionalString(item?.filename || item?.name),
        mediaType: this.toOptionalString(item?.mediaType || item?.mimeType || item?.type),
      }))
      .filter((item) => !!(item.filename || item.fileId || item.fileUrl));
  }

  private unwrapPayload<T>(response: T | { data?: T }): T {
    return (response as any)?.data || response;
  }

  private extractText(source: any, keys: string[]): string {
    for (const key of keys) {
      const value = source?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  private toOptionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }
}
